import { FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox, Switch } from "antd";
import {
    Button,
    Space,
    SimpleCard,
    Typography,
} from "../../../design-system";
import {
    Project,
    ProjectAction,
    RfxVendor,
    RfxVendorStatus,
    canDo,
} from "../../../types/ProjectTypes";
import { CompanyImage } from "../../../common/components/CompanyImage";
import {
    selectRfxVendors,
    setRfxVendorsSelected,
    unselectRfxVendors,
} from "../../../common/api/RfxClient";
import { handleError } from "../../../common/ErrorHandling";
import { SpawnDealModal } from "./SpawnDealModal";
import styles from "./CompletedRfxView.module.less";

interface CompletedRfxViewProps {
    project: Project;
    onUpdate: () => void;
}

/**
 * The vendor outcome of a COMPLETED RFx project: whether vendors were
 * selected and which, over the evaluated candidates. The user answered in
 * the closing survey and can change their mind here: flip the answer, pick
 * or unpick vendors, and spawn a deal-analysis project per selected vendor.
 * The selection lives on the RFx's vendor rows only.
 */
export const CompletedRfxView: FC<CompletedRfxViewProps> = ({
    project,
    onUpdate,
}) => {
    const rfx = project.rfx;
    const vendors = rfx?.vendors ?? [];
    const canEdit = canDo(project, ProjectAction.EDIT);
    const selected = vendors.filter((v) => v.status === RfxVendorStatus.SELECTED);
    const hasDeal = vendors.some((v) => !!v.dealProject);
    const showList = rfx?.vendorsSelected ?? false;
    const [toggling, setToggling] = useState(false);

    if (!rfx || vendors.length === 0) {
        return null;
    }

    const onToggleChange = async (checked: boolean) => {
        if (toggling) return;
        setToggling(true);
        try {
            // Turning the answer off unselects the vendors; the server refuses
            // while a selected vendor has a deal.
            await setRfxVendorsSelected(rfx.id, checked);
            onUpdate();
        } catch (err) {
            handleError(err);
        } finally {
            setToggling(false);
        }
    };

    const outcome = !showList
        ? "No vendor selected"
        : selected.length === 0
          ? "No vendor selected yet"
          : `${selected.length} vendor${selected.length > 1 ? "s" : ""} selected`;

    const header = (
        <div className={styles.header}>
            <span>RFx complete</span>
            <span className={styles.outcome}>{outcome}</span>
            <span className={styles.toggle}>
                <span>Did you select one or more vendors?</span>
                <Switch
                    checked={showList}
                    loading={toggling}
                    disabled={!canEdit || (showList && hasDeal)}
                    onChange={onToggleChange}
                />
            </span>
        </div>
    );

    return (
        <SimpleCard header={header} headerType="primary">
            <div className={showList ? undefined : styles.disabled}>
                <Space direction="vertical" size="middle" fullWidth>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Select the vendor(s) you want to negotiate with. You can create a
                        deal analysis project per vendor once selected.
                    </Typography.Paragraph>
                    <div className={styles.grid}>
                        {vendors.map((v) => (
                            <VendorCard
                                key={v.vendorKey}
                                rfxProject={project}
                                vendor={v}
                                canEdit={canEdit && showList}
                                onChanged={onUpdate}
                            />
                        ))}
                    </div>
                </Space>
            </div>
        </SimpleCard>
    );
};

interface VendorCardProps {
    rfxProject: Project;
    vendor: RfxVendor;
    canEdit: boolean;
    onChanged: () => void;
}

const VendorCard: FC<VendorCardProps> = ({ rfxProject, vendor, canEdit, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const [localStatus, setLocalStatus] = useState<RfxVendorStatus>(vendor.status);
    // The status changes from outside this card too: turning "vendors
    // selected" off resets every vendor, and the refetch must show it.
    useEffect(() => setLocalStatus(vendor.status), [vendor.status]);
    const [spawnModalOpen, setSpawnModalOpen] = useState(false);
    const dealProject = vendor.dealProject;
    const isSelected = localStatus === RfxVendorStatus.SELECTED;

    const onToggle = async (checked: boolean) => {
        if (busy || !canEdit) return;
        const prev = localStatus;
        setLocalStatus(checked ? RfxVendorStatus.SELECTED : RfxVendorStatus.CANDIDATE);
        setBusy(true);
        try {
            const rfxId = rfxProject.rfx!.id;
            if (checked) {
                await selectRfxVendors(rfxId, [vendor.vendorKey]);
            } else {
                await unselectRfxVendors(rfxId, [vendor.vendorKey]);
            }
            onChanged();
        } catch (err) {
            setLocalStatus(prev);
            handleError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <SimpleCard type="white">
                <div className={styles.card}>
                    <div className={styles.checkbox}>
                        <Checkbox
                            checked={isSelected}
                            // A vendor with a deal stays selected: the deal points at it.
                            disabled={!canEdit || busy || !!dealProject}
                            onChange={(e) => onToggle(e.target.checked)}
                        />
                    </div>
                    <div className={styles.logo}>
                        <CompanyImage logo={vendor.vendorHero} size="small" name={vendor.vendorName} />
                    </div>
                    <div className={styles.body}>
                        <div className={styles.name}>{vendor.vendorName}</div>
                        {dealProject && (
                            <Link className={styles.dealLink} to={`/project/${dealProject.id}`}>
                                View deal analysis: {dealProject.name}
                            </Link>
                        )}
                    </div>
                    {isSelected && !dealProject && canEdit && (
                        <Button type="primary" onClick={() => setSpawnModalOpen(true)}>
                            Create deal analysis
                        </Button>
                    )}
                </div>
            </SimpleCard>
            {spawnModalOpen && (
                <SpawnDealModal
                    open={spawnModalOpen}
                    rfxProject={rfxProject}
                    vendor={vendor}
                    onClose={() => setSpawnModalOpen(false)}
                    onSuccess={onChanged}
                />
            )}
        </>
    );
};
